import { ApiError, get, patch, post, type Me } from '../api.js';
import { adviseDraftDay, dobDayOfMonth } from '../../../../packages/core/src/benefits.js';
import { CARRIER_NAMES } from './landing.js';
import {
  ago, button, chip, clear, drawer, el, empty, field, fmtMoney, input, mono, muted, panel,
  row, select, skeleton, table, tile, toast
} from '../ui.js';

/**
 * The book of business.
 *
 * WHY THIS SCREEN IS THE POINT
 * Everything else in Cellvora shows effort: calls placed, leads worked,
 * dispositions recorded. This is the only screen that shows business. The three
 * questions an agency owner asks are what did we write, what is stuck, and who
 * is placing it, and none of them can be answered from a call log.
 *
 * WHY THE BOARD COMES FIRST
 * The default tab is not the list of everything. It is the pending cases and
 * what each one is waiting on, because that is the only part anybody can still
 * change. A case that placed is history and a case that declined is over; a
 * case sitting on an unscheduled paramed exam is a phone call somebody could
 * make this morning.
 */

/* ------------------------------------------------------------ reference -- */

interface Ref {
  policyStatuses: Array<{ code: string; label: string; stage: string }>;
  policyTransitions: Record<string, string[]>;
  productTypes: Array<{ code: string; label: string }>;
  premiumModes: Array<{ code: string; label: string }>;
  underwritingClasses: Array<{ code: string; label: string }>;
  requirementKinds: Array<{ code: string; label: string }>;
  requirementOwners: Array<{ code: string; label: string }>;
}

let refCache: Ref | null = null;
async function reference(): Promise<Ref> {
  if (!refCache) refCache = await get<Ref>('/v1/reference');
  return refCache;
}

const labelOf = (list: Array<{ code: string; label: string }>, code: string): string =>
  list.find((x) => x.code === code)?.label ?? code;

const options = (list: Array<{ code: string; label: string }>): Array<[string, string]> =>
  list.map((x) => [x.code, x.label]);

/* ----------------------------------------------------------------- data -- */

interface Policy {
  id: string; leadId: string | null; carrierId: string | null; agentId: string | null;
  policyNumber: string | null; applicationNumber: string | null;
  productName: string | null; productType: string; termYears: number | null;
  faceCents: number | null; premiumCents: number | null; premiumMode: string;
  /** Day of the month the premium is taken. 1–28, or null if nobody has said. */
  draftDay: number | null;
  annualPremiumCents: number; targetPremiumCents: number | null;
  status: string; underwritingClass: string; ratingTable: string | null; tobaccoRated: boolean | null;
  insuredName: string | null; insuredDob: string | null; beneficiarySummary: string | null;
  isReplacement: boolean;
  quotedAt: string | null; submittedAt: string | null; approvedAt: string | null;
  issuedAt: string | null; placedAt: string | null; effectiveAt: string | null;
  terminatedAt: string | null; outcomeReason: string | null;
  commissionRateBps: number | null; commissionCents: number | null;
  commissionPaidAt: string | null; chargebackCents: number | null;
  notes: string | null; createdAt: string; updatedAt: string;
}

interface Requirement {
  id: string; policyId: string; kind: string; detail: string | null; owner: string;
  requestedAt: string; dueAt: string | null; receivedAt: string | null; waivedAt: string | null;
  lastChasedAt: string | null; chaseCount: number; notes: string | null;
}

interface DraftAdvice {
  verdict: 'safe' | 'tight' | 'fails' | 'unknown';
  safeDays: number[];
  arrival: { earliest: number; latest: number; label: string } | null;
  message: string;
}

interface View {
  policy: Policy; carrierName: string | null; agentName: string | null; leadName: string | null;
  stage: string; statusLabel: string; outstanding: Requirement[];
  estimatedCommissionCents: number | null;
  /** Whether the premium is drafted after the client has been paid. */
  draftAdvice: DraftAdvice | null;
}

interface Detail extends View {
  events: Array<{ fromStatus: string | null; toStatus: string; note: string | null; occurredAt: string }>;
  requirements: Requirement[];
}

interface Carrier {
  id: string; name: string; shortName: string | null; archivedAt: string | null;
  amBestRating: string | null; phone: string | null; agentPortalUrl: string | null;
  notes: string | null;
}

/**
 * A person's appointment to write a carrier.
 *
 * A licence says a producer may sell insurance in a state. An appointment says
 * a particular carrier has agreed to let this particular producer sell ITS
 * products, and it carries the writing number the commission is paid against.
 * They are different facts, they expire on different dates, and a producer who
 * holds one without the other cannot place the case.
 */
interface Appointment {
  id: string; carrierId: string; userId: string; writingNumber: string | null;
  states: string[]; effectiveAt: string | null; expiresAt: string | null;
  terminatedAt: string | null; createdAt: string;
}

/** A saved quote. `policyId` is set once somebody says it became this case. */
interface Quote {
  id: string; leadId: string | null; carrierId: string | null; productId: string | null;
  applicantName: string | null; age: number; sex: string; tobacco: boolean;
  state: string | null; faceCents: number; termYears: number | null;
  rateClass: string | null; monthlyCents: number; annualCents: number;
  source: 'card' | 'entered'; notes: string | null;
  policyId: string | null; createdAt: string;
}

interface Chase {
  requirement: Requirement; policy: Policy;
  carrierName: string | null; agentName: string | null; daysOutstanding: number;
}

interface SourceReturnRow {
  key: string; label: string; kind: string;
  leads: number; costCents: number; placed: number; annualPremiumCents: number;
  costPerPlacedCents: number | null; returnRatio: number | null; tooEarly: boolean;
}

interface Production {
  totals: Totals; pending: Totals; placed: Totals;
  byStage: Record<string, number>; placementRate: number | null;
  byAgent: Row[]; byCarrier: Row[]; byProduct: Row[]; byMonth: Row[];
  bySource: SourceReturnRow[];
}
interface Totals { count: number; annualPremiumCents: number; faceCents: number }
interface Row { key: string; label: string; totals: Totals }

interface RiskBand { cases: number; annualPremiumCents: number; commissionCents: number | null }
interface AtRiskCase {
  policyId: string; leadId: string; insuredName: string | null; agentName: string | null;
  carrierName: string | null; annualPremiumCents: number; commissionCents: number | null;
  draftDay: number | null; safeDays: number[]; verdict: string; monthsInForce: number | null;
}
interface CommissionAtRisk {
  windowMonths: number;
  failing: RiskBand; tight: RiskBand; safe: RiskBand; unknown: RiskBand;
  withoutRate: number;
  worst: AtRiskCase[];
}

/* --------------------------------------------------------------- money -- */

/**
 * Dollars in the boxes, cents in the database.
 *
 * Nobody types 25000000 for a $250,000 policy, and nobody should have to. The
 * conversion happens here and only here, so there is one place for it to be
 * wrong rather than one per form.
 */
function toCents(raw: string): number | null {
  const s = raw.replace(/[$,\s]/g, '').trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}
const fromCents = (c: number | null): string => (c === null ? '' : String(c / 100));

/** "the 1st", "the 22nd" — how a person reads a day of the month back. */
function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

/** A date input wants `2027-03-31`, and an API timestamp is longer than that. */
const dateValue = (iso: string | null): string => (iso ? iso.slice(0, 10) : '');

const STAGE_CHIP: Record<string, string> = {
  pending: 'warn', placed: 'good', lost: 'crit', terminated: 'cop',
};

/* ----------------------------------------------------------------- view -- */

export function casesView(me: Me, tab: string, go: (p: string) => void): HTMLElement {
  const host = el('div');
  const content = el('div');

  host.append(el('div', { class: 'vhead' },
    el('span', { class: 'kicker', text: 'Book of business' }),
    el('h1', { text: 'Cases' }),
    el('p', {
      text: me.capabilities.includes('lead.read.all')
        ? 'Every application and policy in the agency, what each one is waiting on, and what it all adds up to.'
        : 'The cases credited to you. Your own book, and nobody else’s.',
    })));

  const tabs: Array<[string, string]> = [
    ['board', 'What is pending'],
    ['all', 'Everything'],
    ['production', 'Production'],
  ];
  // Tabs on the left, the one action on the right. In a single equal-width row
  // "New case" reads as a fourth tab, and the difference between changing what
  // you are looking at and creating a record is not one to make somebody
  // discover by clicking.
  const nav = el('div', { style: 'display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:14px' });
  const tabRow = el('div', { style: 'display:flex;flex-wrap:wrap;gap:8px;flex:1 1 auto' });
  for (const [key, label] of tabs) {
    tabRow.append(button(label, tab === key ? 'pri sm' : 'sm ghost', () => go(`/cases/${key}`)));
  }
  nav.append(tabRow, button('New case', 'sm', () => void openEditor(null, () => go(`/cases/${tab}`))));
  host.append(nav, content);

  content.append(skeleton(5));
  void (async () => {
    try {
      await reference();
      clear(content);
      if (tab === 'production') content.append(await productionPanels());
      else if (tab === 'all') content.append(await listPanel());
      else content.append(await boardPanels());
    } catch (e) {
      clear(content);
      content.append(el('div', { class: 'warnbox' },
        e instanceof ApiError ? e.message : 'Could not load the book of business.'));
    }
  })();

  return host;
}

/* ---------------------------------------------------------------- board -- */

async function boardPanels(): Promise<HTMLElement> {
  const host = el('div', { class: 'grid' });

  /**
   * Redraws the board in place.
   *
   * Passed down to anything that changes a case, so marking a requirement
   * received updates this screen rather than reloading the page. On a floor
   * where somebody works down the chase list one row at a time, a full reload
   * per row means losing the scroll position twenty times in a morning.
   */
  const draw = async (): Promise<void> => {
    clear(host);
    host.append(skeleton(4));
    const [cases, chases, placed] = await Promise.all([
      get<View[]>('/v1/policies?stage=pending&limit=500'),
      get<Chase[]>('/v1/requirements?limit=300'),
      // In-force business, for the draft-date panel below. Pending cases are
      // covered by `cases`; a case that has not placed yet cannot lapse.
      get<View[]>('/v1/policies?stage=placed&limit=500').catch(() => [] as View[]),
    ]);
    const ref = await reference();
    clear(host);

    if (!cases.length) {
      host.append(panel(null, empty(
        'Nothing is pending',
        'When a case is submitted to a carrier it appears here, with whatever it is waiting on, '
        + 'until it places or it does not.',
        button('Enter a case', 'pri', () => void openEditor(null, () => void draw())),
      )));
      return;
    }

    const order = ref.policyStatuses.filter((s) => s.stage === 'pending').map((s) => s.code);
    const byStatus = new Map<string, View[]>();
    for (const v of cases) {
      const list = byStatus.get(v.policy.status);
      if (list) list.push(v); else byStatus.set(v.policy.status, [v]);
    }

    const tiles = el('div', { class: 'tiles' });
    tiles.append(tile('Pending cases', String(cases.length), 'still undecided'));
    tiles.append(tile('Annualised', fmtMoney(cases.reduce((a, v) => a + v.policy.annualPremiumCents, 0)),
      'if all of it places', 'var(--copper)'));
    tiles.append(tile('Waiting on something', String(chases.length),
      chases.length ? `oldest ${chases[0]!.daysOutstanding} days` : 'nothing outstanding', 'var(--crit)'));
    host.append(tiles);

    // The chase list first. It is the only thing on this screen that somebody
    // can act on in the next ten minutes.
    if (chases.length) host.append(panel('Waiting on something', chaseTable(chases, draw)));

    /*
     * EVERY POLICY WHOSE PREMIUM COMES OUT BEFORE THE CLIENT IS PAID.
     *
     * This is the panel the whole draft-date feature exists for. One case at a
     * time the question is mildly interesting; across a whole book it is a list
     * of the policies that are going to lapse, months before they do, with
     * enough time to move the date and keep the business.
     *
     * Roughly seven in ten life lapses are non-payment rather than a decision
     * to cancel, and the largest single named cause in the peer-reviewed work
     * is forgetfulness rather than affordability. Nothing else on this screen
     * saves a policy that is already sold.
     *
     * Pending and in-force together: a pending case is worth fixing before it
     * places, which is the cheapest moment there is.
     */
    const atRisk = [...cases, ...placed]
      .filter((v) => v.draftAdvice?.verdict === 'fails' || v.draftAdvice?.verdict === 'tight')
      .sort((a, b) => (a.draftAdvice!.verdict === 'fails' ? 0 : 1) - (b.draftAdvice!.verdict === 'fails' ? 0 : 1));
    if (atRisk.length) host.append(panel(
      `Drafting before the money lands — ${atRisk.length}`,
      el('p', {
        style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:10px',
        text: 'These premiums are taken before the client has been paid that month. Nothing is wrong '
          + 'with the policy or the price; the date is wrong, and it is the most common reason a '
          + 'policy this agency wrote correctly still lapses. Moving it costs a phone call.',
      }),
      table(['Client', 'Case', 'Drafted', 'What happens', 'Move it to'], atRisk.map((v) => {
        const a = v.draftAdvice!;
        return {
          cells: [
            v.leadName ?? v.policy.insuredName ?? '—',
            button(v.policy.policyNumber || v.policy.applicationNumber || 'Open', 'sm ghost',
              () => void openCase(v.policy.id, draw)),
            v.policy.draftDay ? ordinal(v.policy.draftDay) : '—',
            chip(a.verdict === 'fails' ? 'every month' : 'some months',
              a.verdict === 'fails' ? 'crit' : 'warn'),
            el('div', { style: 'display:flex;gap:4px;flex-wrap:wrap' },
              ...a.safeDays.slice(0, 3).map((day) => chip(ordinal(day), 'good'))),
          ],
        };
      })),
    ));

    for (const status of order) {
      const rows = byStatus.get(status);
      if (!rows?.length) continue;
      host.append(panel(
        `${labelOf(ref.policyStatuses, status)} — ${rows.length}`,
        caseTable(rows, draw),
      ));
    }
  };

  await draw();
  return host;
}

function chaseTable(chases: Chase[], again: () => Promise<void>): HTMLElement {
  const ref = refCache!;
  return table(
    ['Waiting on', 'Who owes it', 'Case', 'Days', 'Chased', ''],
    chases.map((ch) => ({
      cells: [
        el('div', null,
          el('b', { text: labelOf(ref.requirementKinds, ch.requirement.kind) }),
          ch.requirement.detail ? el('div', { class: 's', style: 'font-size:11.5px;color:var(--ink-3)', text: ch.requirement.detail }) : null),
        labelOf(ref.requirementOwners, ch.requirement.owner),
        button(ch.policy.insuredName || ch.policy.policyNumber || 'Open', 'sm ghost',
          () => void openCase(ch.policy.id, again)),
        // Days outstanding is the number that makes somebody pick up the
        // phone, so it is coloured rather than merely printed.
        chip(String(ch.daysOutstanding),
          ch.daysOutstanding >= 21 ? 'crit' : ch.daysOutstanding >= 10 ? 'warn' : ''),
        ch.requirement.chaseCount
          ? `${ch.requirement.chaseCount}× · ${ago(ch.requirement.lastChasedAt)}`
          : muted('never'),
        el('div', { style: 'display:flex;gap:6px' },
          button('Chased', 'sm', async () => {
            try {
              await post(`/v1/requirements/${ch.requirement.id}/chase`);
              toast('Noted.', 'ok');
              await again();
            } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not record that.', 'crit'); }
          }),
          button('Received', 'sm pri', async () => {
            try {
              await post(`/v1/requirements/${ch.requirement.id}/received`, {});
              toast('Marked received.', 'ok');
              await again();
            } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not record that.', 'crit'); }
          })),
      ],
    })),
  );
}

function caseTable(rows: View[], again: () => Promise<void>): HTMLElement {
  return table(
    ['Insured', 'Carrier', 'Product', 'Annual premium', 'Face', 'Waiting on', 'Producer'],
    rows.map((v) => ({
      onclick: () => void openCase(v.policy.id, again),
      cells: [
        el('div', null,
          el('b', { text: v.policy.insuredName || v.leadName || 'Unnamed case' }),
          v.policy.policyNumber
            ? el('div', { class: 'mono', style: 'font-size:11px;color:var(--ink-4)', text: v.policy.policyNumber })
            : null),
        v.carrierName ? el('span', { text: v.carrierName }) : muted('not set'),
        labelOf(refCache!.productTypes, v.policy.productType),
        mono(fmtMoney(v.policy.annualPremiumCents)),
        mono(fmtMoney(v.policy.faceCents)),
        v.outstanding.length
          ? chip(`${v.outstanding.length} outstanding`, v.outstanding.length > 2 ? 'crit' : 'warn')
          : muted('nothing'),
        v.agentName ?? '—',
      ],
    })),
  );
}

/* ----------------------------------------------------------- everything -- */

async function listPanel(): Promise<HTMLElement> {
  const host = el('div');
  const ref = await reference();
  const results = el('div');

  const stage = select([
    ['', 'Any stage'],
    ['pending', 'Pending — still undecided'],
    ['placed', 'Placed — in force'],
    ['lost', 'Lost — never placed'],
    ['terminated', 'Ended — placed, then ended'],
  ]);
  const status = select([['', 'Any status'], ...options(ref.policyStatuses)]);
  const carrier = select([['', 'Any carrier']]);

  void (async () => {
    const carriers = await get<Carrier[]>('/v1/carriers').catch(() => []);
    for (const c of carriers) carrier.append(el('option', { value: c.id }, c.name));
  })();

  for (const control of [stage, status, carrier]) control.addEventListener('change', () => void load());

  host.append(el('div', { class: 'filters' },
    field('Stage', stage), field('Status', status), field('Carrier', carrier)));
  host.append(results);
  void load();

  async function load(): Promise<void> {
    clear(results);
    results.append(skeleton(6));
    const p = new URLSearchParams({ limit: '500' });
    if (stage.value) p.set('stage', stage.value);
    if (status.value) p.set('status', status.value);
    if (carrier.value) p.set('carrierId', carrier.value);
    const rows = await get<View[]>(`/v1/policies?${p}`).catch(() => [] as View[]);
    clear(results);
    if (!rows.length) {
      results.append(panel(null, empty('Nothing here',
        'No case matches that. Clear the filters, or enter the business you already have.',
        button('Enter a case', 'pri', () => void openEditor(null, () => void load())))));
      return;
    }
    results.append(panel(`${rows.length} case${rows.length === 1 ? '' : 's'}`, table(
      ['Insured', 'Status', 'Carrier', 'Annual premium', 'Placed', 'Producer'],
      rows.map((v) => ({
        onclick: () => void openCase(v.policy.id, load),
        cells: [
          el('div', null,
            el('b', { text: v.policy.insuredName || v.leadName || 'Unnamed case' }),
            v.policy.policyNumber
              ? el('div', { class: 'mono', style: 'font-size:11px;color:var(--ink-4)', text: v.policy.policyNumber })
              : null),
          chip(v.statusLabel, STAGE_CHIP[v.stage] ?? ''),
          v.carrierName ?? '—',
          mono(fmtMoney(v.policy.annualPremiumCents)),
          v.policy.placedAt ? el('span', { text: dateValue(v.policy.placedAt) }) : muted('—'),
          v.agentName ?? '—',
        ],
      })),
    )));
  }

  return host;
}

/* ---------------------------------------------------------- draft dates -- */

interface DraftReport {
  exposure: { checked: number; fails: number; tight: number; safe: number; unknown: number };
  liveCases: number;
  moves: { improved: number; worsened: number; noChange: number; notComparable: number };
  improvedStillInForce: { total: number; inForce: number };
  reviewed: number;
}

/**
 * What the draft-date engine found, and what was done about it.
 *
 * WHY THIS PANEL EXISTS AND WHAT IT IS FOR
 * Everything beside it on this screen measures production, which every agency
 * management system in the industry measures. This measures the thing none of
 * them do — and it is the number an agency owner needs in order to believe any
 * of this was worth doing.
 *
 * WHAT IT DELIBERATELY DOES NOT CLAIM
 * That the drafts would have failed, or that moving them is why the policies
 * survived. Cellvora has no feed of carrier NSF events and cannot get one. It
 * says "still in force", never "saved", and the note at the bottom says why in
 * plain words. Overclaiming here would poison the one number the product has
 * that nobody else does, and the first agency to check its own file would stop
 * believing every other number on the screen too.
 */
function draftPanel(since: HTMLInputElement, until: HTMLInputElement): HTMLElement {
  const body = el('div');

  async function load(): Promise<void> {
    clear(body);
    body.append(skeleton(2));
    const p = new URLSearchParams();
    if (since.value) p.set('since', since.value);
    if (until.value) p.set('until', `${until.value}T23:59:59Z`);
    let r: DraftReport;
    try {
      r = await get<DraftReport>(`/v1/draft-report?${p}`);
    } catch (e) {
      clear(body);
      body.append(el('div', { class: 'warnbox' },
        e instanceof ApiError ? e.message : 'Could not load the draft-date report.',
        el('div', { style: 'margin-top:10px' }, button('Try again', 'sm', () => void load()))));
      return;
    }
    clear(body);

    if (!r.liveCases) {
      body.append(empty('No policies in force yet',
        'Once cases are placed, this shows how many of them draft on a day the client has not '
        + 'been paid — which is the largest single cause of a policy dying that nobody wrote down.'));
      return;
    }

    const tiles = el('div', { class: 'tiles' });
    tiles.append(tile(
      'Drafting too early', String(r.exposure.fails),
      `of ${r.exposure.checked} that could be checked`,
      r.exposure.fails ? 'var(--crit)' : 'var(--good)',
    ));
    tiles.append(tile('Cutting it fine', String(r.exposure.tight), 'draft lands inside the pay window', 'var(--warn)'));
    tiles.append(tile('Clear', String(r.exposure.safe), 'money is there first', 'var(--good)'));
    // Reported beside the rest, not folded into it. A book where nothing is
    // known reads as a book with no problem if you only count failures, and
    // that is exactly backwards.
    tiles.append(tile(
      'Nobody has asked', String(r.exposure.unknown),
      'no pay schedule on file', r.exposure.unknown ? 'var(--copper)' : 'var(--ink-4)',
    ));
    body.append(tiles);

    if (r.reviewed === 0) {
      body.append(el('p', {
        style: 'font-size:12.5px;color:var(--ink-3);margin-top:14px',
        text: 'No draft dates have been moved yet. When one is, it is recorded here with what the '
          + 'engine said before and after, so the agency can see what came of it.',
      }));
    } else {
      const t2 = el('div', { class: 'tiles', style: 'margin-top:14px' });
      t2.append(tile('Draft dates moved', String(r.reviewed), 'in this period'));
      t2.append(tile('Onto a better day', String(r.moves.improved), 'off a day the money was not there', 'var(--good)'));
      if (r.moves.worsened) {
        t2.append(tile('Onto a worse day', String(r.moves.worsened), 'worth a look', 'var(--crit)'));
      }
      t2.append(tile(
        'Of those, still in force',
        `${r.improvedStillInForce.inForce} of ${r.improvedStillInForce.total}`,
        'not a claim about cause — see below',
      ));
      body.append(t2);
    }

    body.append(el('p', {
      style: 'font-size:12px;color:var(--ink-4);margin-top:14px;line-height:1.5',
      text: 'What this shows: that a draft day was out of step with when the client is paid, that '
        + 'somebody moved it, and whether the policy is still in force. What it does not show is '
        + 'that the draft would have failed, or that moving it is why the policy survived — '
        + 'Cellvora sees no carrier payment events and does not pretend to.',
    }));
  }

  void load();
  for (const c of [since, until]) c.addEventListener('change', () => void load());
  return body;
}

/* ----------------------------------------------------------- production -- */

async function productionPanels(): Promise<HTMLElement> {
  const host = el('div', { class: 'grid' });
  const since = input({ type: 'date' });
  const until = input({ type: 'date' });
  const body = el('div', { class: 'grid' });

  for (const control of [since, until]) control.addEventListener('change', () => void load());
  host.append(el('div', { class: 'filters' }, field('From', since), field('To', until)));
  /*
   * First on the screen, and deliberately outside the date filter.
   *
   * Every other panel here answers "how did we do over this period". This one
   * answers "what is wrong right now, and whose money is it" — a question with
   * no date range, because a policy drafting on the wrong day is drafting on
   * the wrong day whatever window you were looking at.
   */
  const risk = el('div');
  host.append(risk);
  async function loadRisk(): Promise<void> {
    clear(risk);
    risk.append(skeleton(1));
    let r: CommissionAtRisk;
    try {
      r = await get<CommissionAtRisk>('/v1/commission-at-risk');
    } catch (e) {
      clear(risk);
      risk.append(el('div', { class: 'warnbox' },
        e instanceof ApiError ? e.message : 'Could not load what the draft days are costing.',
        el('div', { style: 'margin-top:10px' }, button('Try again', 'sm', () => void loadRisk()))));
      return;
    }
    clear(risk);
    const built = atRiskPanel(r, loadRisk);
    if (built) risk.append(built);
  }
  void loadRisk();

  // Above production, on purpose. Production is what every agency management
  // system reports; this is the one nobody else can.
  host.append(panel('Draft dates — where the money is not there yet', draftPanel(since, until)));
  host.append(body);
  // Below production, also on purpose. This is a report on the software, not on
  // the agency, and an owner who opened this tab came to see business first.
  host.append(panel('How often the call reader was right', readerPanel(since)));
  void load();

  async function load(): Promise<void> {
    clear(body);
    body.append(skeleton(4));
    const p = new URLSearchParams();
    if (since.value) p.set('since', since.value);
    if (until.value) p.set('until', `${until.value}T23:59:59Z`);
    // Always one template literal, even when the query is empty: a URL assembled
    // by a conditional is a URL that cannot be found by reading the source, and
    // the wiring test that proves this screen actually calls the API reads the
    // source.
    //
    // Caught, because `load()` is called with `void` and nothing above awaits
    // it. Unhandled, a failure here left the skeleton on screen forever: no
    // error, no retry, and indistinguishable from still loading. Of the three
    // tabs on this screen it was the only one that could do that.
    let rep: Production;
    try {
      rep = await get<Production>(`/v1/production?${p}`);
    } catch (e) {
      clear(body);
      body.append(el('div', { class: 'warnbox' },
        e instanceof ApiError ? e.message : 'Could not load the production report.',
        el('div', { style: 'margin-top:10px' }, button('Try again', 'sm', () => void load()))));
      return;
    }
    clear(body);

    const tiles = el('div', { class: 'tiles' });
    tiles.append(tile('Placed', String(rep.placed.count), 'cases that counted', 'var(--good)'));
    tiles.append(tile('Annualised premium', fmtMoney(rep.placed.annualPremiumCents), 'on placed business', 'var(--copper)'));
    tiles.append(tile('Face placed', fmtMoney(rep.placed.faceCents), 'coverage written'));
    tiles.append(tile('Pending', String(rep.pending.count), fmtMoney(rep.pending.annualPremiumCents), 'var(--warn)'));
    tiles.append(tile(
      'Placement rate',
      // Null, not nought. An agency with nothing decided does not have a
      // placement rate of 0% — it has no placement rate, and showing 0% reads
      // as catastrophe on the first week of use.
      rep.placementRate === null ? '—' : `${Math.round(rep.placementRate * 100)}%`,
      rep.placementRate === null ? 'nothing decided yet' : 'of business that settled',
      'var(--iris)',
    ));
    body.append(tiles);

    const breakdown = (title: string, rows: Row[], head: string): HTMLElement | null => {
      if (!rows.length) return null;
      return panel(title, table([head, 'Cases', 'Annualised premium', 'Face'], rows.map((r) => ({
        cells: [
          r.label,
          String(r.totals.count),
          mono(fmtMoney(r.totals.annualPremiumCents)),
          mono(fmtMoney(r.totals.faceCents)),
        ],
      }))));
    };

    // Above the breakdowns. Production by producer, carrier and product is what
    // every agency system reports; this is the one that decides next month's
    // budget, and burying it under three familiar tables would be a choice.
    if (rep.bySource.length) body.append(sourceReturnPanel(rep.bySource));

    /*
     * And above "by producer" for the same reason. That table says who is
     * behind; this one says which of three different things is wrong, which is
     * the part that changes what somebody does on Monday.
     *
     * Loaded separately and allowed to fail on its own — it is an addition to
     * a production report that worked before it existed, and a floor with no
     * recorded calls simply has nothing to show.
     */
    try {
      const built = floorPanel(await get<Scoreboard>('/v1/scoreboard'));
      if (built) body.append(built);
    } catch { /* the production report stands without it */ }

    const parts = [
      breakdown('By producer', rep.byAgent, 'Producer'),
      breakdown('By carrier', rep.byCarrier, 'Carrier'),
      breakdown('By product', rep.byProduct.map((r) => ({
        ...r, label: labelOf(refCache!.productTypes, r.key),
      })), 'Product'),
      breakdown('By month placed', rep.byMonth, 'Month'),
    ].filter(Boolean) as HTMLElement[];

    if (!parts.length) {
      body.append(panel(null, empty('Nothing has placed yet',
        'Production counts business that placed, not everything entered. As cases go in force they '
        + 'appear here, split by producer, carrier, product and month.')));
      return;
    }
    for (const part of parts) body.append(part);
  }

  return host;
}

/* -------------------------------------------------------- reader score -- */

interface Accuracy {
  accepted: number;
  rejected: number;
  fields: Array<{ field: string; proposed: number; keptAsRead: number; corrected: number }>;
}

const FIELD_LABEL: Record<string, string> = {
  outcome: 'What the call produced',
  carrier: 'Carrier',
  premium: 'Premium',
  coverage: 'Coverage',
};

/**
 * Below how many readings a percentage is noise rather than a measurement.
 *
 * Three readings, two right, is not "67% accurate" — it is three readings. A
 * figure shown at that sample size will be quoted at that sample size, and the
 * first person to quote it will be an owner deciding how much checking their
 * agents have to do.
 */
const ENOUGH = 20;

/**
 * How often the call reader agreed with the person who checked it.
 *
 * WHY THIS SCREEN EXISTS AT ALL
 * Every confirmed reading already stores both the proposal and what the person
 * actually confirmed. The gap between them is a free, honest accuracy score
 * that nothing was reading. Without it, the only answer to "can we trust this
 * thing yet" is a feeling — and feelings about software one has built are not
 * evidence.
 *
 * WHY IT IS PER FIELD
 * "The reader is 80% accurate" is not actionable. "It reads the premium right
 * 95% of the time and the carrier right 60%" says what to fix, and says which
 * half of a reading an agent should actually look at.
 */
function readerPanel(since: HTMLInputElement): HTMLElement {
  const body = el('div');

  async function load(): Promise<void> {
    clear(body);
    body.append(skeleton(2));
    const p = new URLSearchParams();
    if (since.value) p.set('since', since.value);
    let r: Accuracy;
    try {
      r = await get<Accuracy>(`/v1/call-readings/accuracy?${p}`);
    } catch (e) {
      clear(body);
      body.append(el('div', { class: 'warnbox' },
        e instanceof ApiError ? e.message : 'Could not load the reader score.',
        el('div', { style: 'margin-top:10px' }, button('Try again', 'sm', () => void load()))));
      return;
    }
    clear(body);

    const decided = r.accepted + r.rejected;
    if (!decided) {
      body.append(empty('Nothing has been confirmed yet',
        'Every time somebody confirms or rejects what the system read off a call, the difference '
        + 'between what it proposed and what they agreed is recorded here. It is the only evidence '
        + 'that would ever justify asking agents to check less than they check now.'));
      return;
    }

    const tiles = el('div', { class: 'tiles' });
    tiles.append(tile('Confirmed', String(r.accepted), 'readings a person agreed with', 'var(--good)'));
    tiles.append(tile('Sent back', String(r.rejected), 'readings a person rejected',
      r.rejected ? 'var(--warn)' : undefined));
    tiles.append(tile(
      'Agreed with',
      // Below the threshold this is a count, not a rate, and it says so rather
      // than printing a number that would be repeated as if it meant something.
      decided < ENOUGH ? '—' : `${Math.round((r.accepted / decided) * 100)}%`,
      decided < ENOUGH ? `${decided} of ${ENOUGH} readings needed` : 'of everything it read',
      'var(--iris)',
    ));
    body.append(tiles);

    const scored = r.fields.filter((f) => f.proposed > 0);
    if (scored.length) {
      body.append(table(
        ['Field', 'Times it said something', 'Kept as read', 'Corrected', 'Right'],
        scored.map((f) => ({
          cells: [
            FIELD_LABEL[f.field] ?? f.field,
            String(f.proposed),
            String(f.keptAsRead),
            String(f.corrected),
            f.proposed < ENOUGH
              ? muted('too few to say')
              : mono(`${Math.round((f.keptAsRead / f.proposed) * 100)}%`),
          ],
        })),
      ));
    }

    body.append(el('div', {
      class: 'note',
      style: 'margin-top:12px',
      text: 'A field only counts on the calls where the reader actually proposed something. '
        + 'Leaving the carrier blank rather than guessing it is the behaviour, not a miss — '
        + 'scoring blanks as correct would make a reader that says nothing look perfect. '
        + 'A rejected reading counts as rejected and against no field in particular, because '
        + '“it got it wrong” does not say which part was wrong.',
    }));
    body.append(el('div', {
      class: 'note',
      style: 'margin-top:8px',
      text: 'What this measures is agreement with the person who checked it, which is not the '
        + 'same thing as being right. A confirmation is one agent, at speed, saying it looks correct.',
    }));
  }

  since.addEventListener('change', () => void load());
  void load();
  return body;
}

/* ---------------------------------------------------------- the floor -- */

interface ScoreRow {
  userId: string; name: string;
  dials: number; contacts: number; contactRate: number | null;
  casesOpened: number; placed: number; annualPremiumCents: number;
  contactsPerCase: number | null;
  finding: 'too_early' | 'fine' | 'dials' | 'reach' | 'close';
  note: string | null;
}
interface Scoreboard {
  windowDays: number; thin: boolean; rows: ScoreRow[];
  floor: {
    people: number; dials: number; contacts: number; contactRate: number | null;
    casesOpened: number; placed: number; annualPremiumCents: number;
    medianDials: number; medianContactRate: number | null;
  };
}

const FINDING_LABEL: Record<ScoreRow['finding'], [string, string]> = {
  too_early: ['Too early to say', ''],
  fine: ['Nothing to flag', 'ok'],
  dials: ['Not enough calls', 'warn'],
  reach: ['List or hours', 'cop'],
  close: ['The conversation', 'crit'],
};

/**
 * Which of three things is going wrong, per person.
 *
 * WHY THIS SITS ABOVE "BY PRODUCER"
 * Production by producer is what every agency system reports. It tells an owner
 * who is behind and nothing about why — and the answer to "why" is always one
 * of exactly three things, needing three different conversations: not enough
 * calls, enough calls and not enough people reached, enough people reached and
 * nothing opened. The first is effort. The second is the list or the hours. The
 * third is the pitch, and it is the only one where sitting in on a few calls
 * helps.
 *
 * Getting that wrong is expensive in a way nobody measures. "Get on the phone",
 * said to somebody already dialling two hundred times a day and reaching
 * nobody, burns more of a list that is not working and tells a person who is
 * trying that trying is the problem.
 *
 * WHY IT IS NOT A LEAGUE TABLE
 * An agent sees their own row and the floor's aggregate to sit it against, not
 * a list of colleagues sorted by sales. The purpose is to tell one person what
 * to fix; a leaderboard tells everybody else who is losing, which is a
 * different product with different effects on a floor.
 */
function floorPanel(b: Scoreboard): HTMLElement | null {
  if (b.floor.dials === 0) return null;

  const rate = (r: number | null): Node =>
    (r === null ? muted('—') : mono(`${Math.round(r * 100)}%`));

  const tiles = el('div', { class: 'tiles' });
  tiles.append(tile('Dials', b.floor.dials.toLocaleString(), `over ${b.windowDays} days`, 'var(--iris)'));
  tiles.append(tile(
    'Conversations', b.floor.contacts.toLocaleString(),
    // Null, not nought — a floor with forty dials has no contact rate yet, and
    // printing 0% in week one reads as catastrophe.
    b.floor.contactRate === null ? 'not enough to rate yet' : `${Math.round(b.floor.contactRate * 100)}% of dials`,
    'var(--good)',
  ));
  tiles.append(tile('Cases opened', String(b.floor.casesOpened), `${b.floor.placed} placed`, 'var(--copper)'));

  const rows = b.rows.map((r) => {
    const [label, tone] = FINDING_LABEL[r.finding];
    return {
      cells: [
        el('div', null,
          el('div', { text: r.name }),
          r.note ? el('div', { style: 'font-size:12px;color:var(--ink-4);max-width:46ch', text: r.note }) : null),
        mono(r.dials.toLocaleString()),
        mono(r.contacts.toLocaleString()),
        rate(r.contactRate),
        mono(String(r.casesOpened)),
        mono(fmtMoney(r.annualPremiumCents)),
        r.finding === 'fine' ? muted(label) : chip(label, tone),
      ],
    };
  });

  return panel('The floor, and what to say to whom',
    tiles,
    b.thin
      ? el('div', { class: 'note', style: 'margin-top:14px' },
        el('b', null, 'Too few calls so far to say anything about anybody. '),
        'A hundred dials across three people will happily produce a worst performer, and it '
        + 'will have been decided by who caught somebody at lunch.')
      : el('div', { style: 'margin-top:16px' },
        table(['Who', 'Dials', 'Spoke to', 'Reached', 'Cases', 'Annualised', 'Worth a word about'], rows)),
    el('p', {
      style: 'font-size:12px;color:var(--ink-4);margin-top:14px;line-height:1.5',
      text: 'One finding per person, in order: calls, then people reached, then cases opened. '
        + 'You cannot judge somebody\'s pitch if they are not reaching anybody, so the test stops '
        + 'at the first thing that is off. Measured against the floor\'s median — '
        + `${Math.round(b.floor.medianDials)} dials`
        + `${b.floor.medianContactRate === null ? '' : `, ${Math.round(b.floor.medianContactRate * 100)}% reached`}`
        + ' — and never an average, so one person\'s big week cannot make everybody else look behind.',
    }));
}

/* ------------------------------------------------------ money at risk -- */

/**
 * The same facts as the draft report, in the only unit anybody acts on.
 *
 * WHY THIS EXISTS WHEN THE DRAFT REPORT ALREADY COUNTS THESE CASES
 * Nobody picks up a phone because three cases are "drafting early". They pick
 * up a phone because $1,840 of their own advance is sitting on a day the
 * client's money has not landed, and the row says which day to move it to.
 * Identical facts, one changes an afternoon.
 *
 * WHY EVERY JUDGEMENT CALL IN IT GOES THE UNFLATTERING WAY
 * An agent will quote this figure — to themselves, to an upline, possibly to a
 * client. So policies past the advance window drop out, cases with no
 * commission rate are named rather than counted as zero, and a band with no
 * rate anywhere shows a dash instead of a total. A number that flatters is
 * worse than no number: it gets believed once, and then never again.
 */
function atRiskPanel(r: CommissionAtRisk, again: () => Promise<void>): HTMLElement | null {
  const exposed = r.failing.cases + r.tight.cases;
  if (!exposed && !r.unknown.cases) return null;

  /* Null plus a number is a number and a gap, never a quiet total. */
  const addComm = (a: number | null, b: number | null): number | null =>
    (a === null && b === null ? null : (a ?? 0) + (b ?? 0));
  const money = addComm(r.failing.commissionCents, r.tight.commissionCents);
  const premium = r.failing.annualPremiumCents + r.tight.annualPremiumCents;

  const tiles = el('div', { class: 'tiles' });
  tiles.append(tile(
    'Your money on a bad day',
    money === null ? '—' : fmtMoney(money),
    money === null ? 'no commission rate on any of them' : `across ${exposed} case${exposed === 1 ? '' : 's'}`,
    exposed ? 'var(--crit)' : 'var(--good)',
  ));
  tiles.append(tile(
    'Premium behind it', fmtMoney(premium), 'annualised, on those cases', 'var(--copper)',
  ));
  tiles.append(tile(
    'Clear',
    r.safe.commissionCents === null ? String(r.safe.cases) : fmtMoney(r.safe.commissionCents),
    `${r.safe.cases} case${r.safe.cases === 1 ? '' : 's'} drafting after the money lands`,
    'var(--good)',
  ));
  if (r.unknown.cases) {
    tiles.append(tile(
      'Nobody has asked', String(r.unknown.cases),
      'no pay schedule on file — not the same as no problem', 'var(--copper)',
    ));
  }

  const fix = (c: AtRiskCase): Node => {
    if (!c.safeDays.length) return muted('no clear day');
    const days = c.safeDays.slice(0, 3).map(ordinal).join(', ');
    return el('span', { class: 'mono', style: 'color:var(--good)', text: days });
  };

  const rows = r.worst.map((c) => ({
    cells: [
      el('div', null,
        el('div', { text: c.insuredName ?? 'Unnamed case' }),
        el('div', {
          style: 'font-size:12px;color:var(--ink-4)',
          text: [c.agentName, c.carrierName].filter(Boolean).join(' · ') || '—',
        })),
      c.verdict === 'fails'
        ? chip('Drafts too early', 'crit')
        : chip('Cutting it fine', 'warn'),
      c.draftDay === null ? muted('—') : mono(ordinal(c.draftDay)),
      fix(c),
      mono(fmtMoney(c.annualPremiumCents)),
      c.commissionCents === null ? muted('no rate') : mono(fmtMoney(c.commissionCents)),
      button('Open', 'sm', () => void openCase(c.policyId, again)),
    ],
  }));

  return panel('What the wrong draft day is about to cost',
    tiles,
    rows.length
      ? el('div', { style: 'margin-top:16px' },
        table(['Case', 'Verdict', 'Drafts', 'Move it to', 'Premium', 'Commission', ''], rows))
      : el('p', {
        style: 'font-size:12.5px;color:var(--ink-3);margin-top:14px',
        text: 'Nothing is drafting before the client is paid. This is the state to stay in.',
      }),
    r.withoutRate
      ? el('div', { class: 'warnbox', style: 'margin-top:14px' },
        el('b', null, `${r.withoutRate} case${r.withoutRate === 1 ? ' has' : 's have'} no commission rate recorded. `),
        'Those cases are counted, but their money is not in the figures above — so the real '
        + 'number is higher than what is shown. Setting the rate on the case fixes it.')
      : null,
    el('p', {
      style: 'font-size:12px;color:var(--ink-4);margin-top:14px;line-height:1.5',
      text: `Exposure, not a prediction. This is commission still inside the ${r.windowMonths}-month `
        + 'advance period sitting on a draft day that falls before the client is paid. Cellvora '
        + 'sees no carrier payment events: it cannot tell you a draft failed, only that it is '
        + 'scheduled for a day the money is not there yet. Cases past the advance window are left '
        + 'out — that money is earned.',
    }));
}

/**
 * What each lead vendor cost, against what it actually wrote.
 *
 * THE REPORT NOBODY ELSE CAN PRODUCE
 * An agency management system knows what placed and never what the lead cost. A
 * dialer knows what the lead cost and never what placed. Both halves have to
 * sit in one database for this to be computable at all, and here they do.
 *
 * WHAT IT REFUSES TO SAY
 * A cost per acquisition off one or two policies. A vendor gets cut on this
 * number, and a figure printed at that sample size gets quoted at that sample
 * size by whoever is deciding. Below the threshold it shows the counts and says
 * "too early" rather than a number somebody will act on.
 */
function sourceReturnPanel(rows: SourceReturnRow[]): HTMLElement {
  const spent = rows.reduce((n, r) => n + r.costCents, 0);
  const written = rows.reduce((n, r) => n + r.annualPremiumCents, 0);

  const ratio = (r: SourceReturnRow): Node => {
    if (r.tooEarly) return muted(`${r.placed} placed — too early`);
    if (r.returnRatio === null) return muted('no cost recorded');
    const good = r.returnRatio >= 1;
    return el('span', {
      class: 'mono',
      style: `color:var(--${good ? 'good' : 'crit'})`,
      text: `${r.returnRatio.toFixed(1)}×`,
    });
  };

  return panel('What each lead source cost, and what it wrote',
    el('div', { class: 'note', style: 'margin-bottom:14px' },
      el('b', null, 'Spend covers every lead ever bought; premium covers this window. '),
      'They are different spans on purpose — you paid for a lead the day it arrived, and a '
      + 'policy written this month may have come from one bought last year. Narrowing either '
      + 'side would make a slow vendor look worse than it is.'),
    table(
      ['Source', 'Leads', 'Spent', 'Placed', 'Annualised', 'Cost each', 'Return'],
      rows.map((r) => ({
        cells: [
          el('div', null, el('div', { text: r.label }),
            el('div', { style: 'font-size:12px;color:var(--ink-4)', text: r.kind })),
          mono(r.leads.toLocaleString()),
          mono(fmtMoney(r.costCents)),
          mono(String(r.placed)),
          mono(fmtMoney(r.annualPremiumCents)),
          r.costPerPlacedCents === null ? muted('—') : mono(fmtMoney(r.costPerPlacedCents)),
          ratio(r),
        ],
      })),
    ),
    spent > 0
      ? el('div', { class: written >= spent ? 'okbox' : 'warnbox', style: 'margin-top:14px' },
        el('b', null, `${fmtMoney(spent)} spent on leads, ${fmtMoney(written)} of annualised premium written. `),
        written >= spent
          ? 'Every dollar of lead spend has come back at least once.'
          : 'Lead spend is ahead of what has been written so far. Early on that is normal — '
            + 'it stops being normal once a source has had enough leads to prove itself.')
      : null);
}

/* ------------------------------------------------------------ one case -- */

/**
  * One case, opened over whatever screen you were on.
  *
  * `onChange` is what keeps that screen honest. Marking a requirement received
  * inside the drawer changes the board behind it, and a board that still shows
  * the old state once the drawer closes is worse than one that never showed it:
  * somebody chases the same thing twice.
  */
async function openCase(id: string, onChange?: () => Promise<void> | void): Promise<void> {
  const body = el('div');
  const close = drawer('Case', body);
  body.append(skeleton(6));

  let d: Detail;
  try {
    d = await get<Detail>(`/v1/policies/${id}`);
  } catch (e) {
    clear(body);
    body.append(el('div', { class: 'warnbox' }, e instanceof ApiError ? e.message : 'Could not open that case.'));
    return;
  }
  const ref = await reference();
  clear(body);

  const refresh = async (): Promise<void> => {
    close();
    await onChange?.();
    await openCase(id, onChange);
  };

  /* -- the headline ------------------------------------------------------ */
  const head = el('div', { class: 'tiles', style: 'margin-bottom:14px' });
  head.append(tile('Status', d.statusLabel, d.stage));
  head.append(tile('Annualised', fmtMoney(d.policy.annualPremiumCents),
    `${fromCents(d.policy.premiumCents) ? `$${fromCents(d.policy.premiumCents)} ` : ''}${labelOf(ref.premiumModes, d.policy.premiumMode).toLowerCase()}`,
    'var(--copper)'));
  head.append(tile('Face', fmtMoney(d.policy.faceCents), labelOf(ref.productTypes, d.policy.productType)));
  if (d.estimatedCommissionCents !== null) {
    head.append(tile('Commission expected', fmtMoney(d.estimatedCommissionCents),
      // The estimate is derived, never stored. Saying so on the tile is what
      // stops it being read as money already earned.
      'an estimate until the statement lands', 'var(--iris)'));
  }
  body.append(head);

  /* -- when the money lands ---------------------------------------------- */
  /*
   * Placed directly under the headline, above everything a person can act on,
   * because a draft taken before the client is paid kills a policy that is
   * otherwise perfectly good — and it does it quietly, months later, as a
   * chargeback on business that was written and priced correctly.
   *
   * Only shown when there is something to say. A case with no client attached,
   * or a client nobody has asked how they are paid, gets nothing here rather
   * than a box explaining its own emptiness on every case in the book.
   */
  if (d.draftAdvice && d.draftAdvice.verdict !== 'unknown') {
    const a = d.draftAdvice;
    const tone = a.verdict === 'fails' ? 'warnbox' : a.verdict === 'tight' ? 'note' : 'okbox';
    const box = el('div', { class: tone },
      el('b', null, a.verdict === 'safe' ? 'The draft date is safe. '
        : a.verdict === 'tight' ? 'The draft date is a coin toss. '
          : 'The draft comes out before they are paid. '),
      a.message);
    if (a.verdict !== 'safe' && a.safeDays.length) {
      box.append(el('div', { style: 'display:flex;gap:6px;flex-wrap:wrap;margin-top:8px' },
        ...a.safeDays.map((day) => chip(ordinal(day), 'good'))));
    }
    body.append(el('div', { style: 'margin-bottom:14px' }, box));
  }

  /* -- moving it on ------------------------------------------------------ */
  const moves = ref.policyTransitions[d.policy.status] ?? [];
  const movePanel = el('div');
  if (moves.length) {
    const note = input({ placeholder: 'Why, in the carrier’s words (optional)' });
    const buttons = el('div', { style: 'display:flex;flex-wrap:wrap;gap:8px;margin-bottom:10px' });
    for (const to of moves) {
      buttons.append(button(labelOf(ref.policyStatuses, to), 'sm', async () => {
        try {
          await post(`/v1/policies/${d.policy.id}/status`, { status: to, note: note.value.trim() || undefined });
          toast('Moved.', 'ok');
          await refresh();
        } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not move that case.', 'crit'); }
      }));
    }
    movePanel.append(
      el('p', {
        style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:10px',
        // Only the legal moves are offered, so nobody has to learn the rules by
        // being refused.
        text: 'Only the moves this case can actually make are shown. Each one stamps its own date and is written to the history below.',
      }),
      buttons, note,
    );
  } else {
    movePanel.append(el('p', {
      style: 'font-size:12.5px;color:var(--ink-3)',
      text: `“${d.statusLabel}” is the end of the line for a case. Nothing follows it.`,
    }));
  }
  body.append(panel('Move it on', movePanel));

  /* -- what it waits on -------------------------------------------------- */
  const reqBody = el('div');
  const outstanding = d.requirements.filter((r) => !r.receivedAt && !r.waivedAt);
  const settled = d.requirements.filter((r) => r.receivedAt || r.waivedAt);

  if (!d.requirements.length) {
    reqBody.append(el('p', {
      style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:12px',
      text: 'Nothing recorded. A case does not sit in underwriting because underwriting is slow — '
        + 'it sits there because something specific has not arrived. Naming it is what gets it chased.',
    }));
  } else {
    reqBody.append(table(['Waiting on', 'Who owes it', 'Asked', 'State', ''],
      [...outstanding, ...settled].map((r) => ({
        cells: [
          el('div', null,
            el('b', { text: labelOf(ref.requirementKinds, r.kind) }),
            r.detail ? el('div', { style: 'font-size:11.5px;color:var(--ink-3)', text: r.detail }) : null),
          labelOf(ref.requirementOwners, r.owner),
          ago(r.requestedAt),
          r.receivedAt ? chip('received', 'good')
            : r.waivedAt ? chip('waived', 'cop')
              : chip(r.chaseCount ? `chased ${r.chaseCount}×` : 'outstanding', 'warn'),
          r.receivedAt || r.waivedAt
            ? button('Reopen', 'sm ghost', async () => {
              try { await post(`/v1/requirements/${r.id}/reopen`); await refresh(); }
              catch (e) { toast(e instanceof ApiError ? e.message : 'Could not reopen it.', 'crit'); }
            })
            : el('div', { style: 'display:flex;gap:6px;flex-wrap:wrap' },
              button('Chased', 'sm ghost', async () => {
                try { await post(`/v1/requirements/${r.id}/chase`); await refresh(); }
                catch (e) { toast(e instanceof ApiError ? e.message : 'Could not record that.', 'crit'); }
              }),
              button('Received', 'sm pri', async () => {
                try { await post(`/v1/requirements/${r.id}/received`, {}); await refresh(); }
                catch (e) { toast(e instanceof ApiError ? e.message : 'Could not record that.', 'crit'); }
              }),
              button('Waived', 'sm ghost', async () => {
                try { await post(`/v1/requirements/${r.id}/waived`, {}); await refresh(); }
                catch (e) { toast(e instanceof ApiError ? e.message : 'Could not record that.', 'crit'); }
              })),
        ],
      }))));
  }

  const kind = select(options(ref.requirementKinds));
  const ownedBy = select(options(ref.requirementOwners), 'client');
  const detail = input({ placeholder: 'Anything specific — “second attempt, no-showed once”' });
  const add = button('Add', 'pri', async () => {
    add.disabled = true;
    try {
      await post(`/v1/policies/${d.policy.id}/requirements`, {
        kind: kind.value, owner: ownedBy.value, detail: detail.value.trim() || undefined,
      });
      await refresh();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not add that.', 'crit');
      add.disabled = false;
    }
  });
  reqBody.append(el('div', { class: 'filters', style: 'margin-top:12px' },
    field('Waiting on', kind), field('Who owes it', ownedBy), field('Detail', detail)), add);
  body.append(panel('What it is waiting on', reqBody));

  /* -- the quote it came from -------------------------------------------- */
  body.append(panel('The quote it came from', await quotesForCase(d, refresh)));

  /* -- the record -------------------------------------------------------- */
  body.append(panel('The record', await editorForm(d.policy, refresh)));

  /* -- history ----------------------------------------------------------- */
  // Cells are Nodes, not strings: `table()` assigns a string cell with
  // innerHTML, and a status note is free text somebody at the agency typed.
  body.append(panel('History', table(['When', 'Moved to', 'Note'], d.events.map((e) => ({
    cells: [
      el('span', { text: new Date(e.occurredAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) }),
      el('span', { text: labelOf(ref.policyStatuses, e.toStatus) }),
      e.note
        ? el('span', { text: e.note })
        : el('span', { style: 'color:var(--ink-4)', text: '—' }),
    ],
  })))));
}

/**
 * Which saved quote became this case.
 *
 * WHY THIS EXISTS
 * Quotes were being saved and never read back. `POST /v1/quotes` was wired to
 * a button; nothing anywhere fetched one again. So an agent quoting a family
 * at the kitchen table could save the numbers and then never see them, and the
 * one measurement an agency owner actually wants from a quoter — how much of
 * what we quote we go on to write — could not be computed at all, because
 * nothing ever set `quote.policyId`.
 *
 * Linking is a claim about history, so it is a person's judgement and not a
 * guess. Cellvora will not infer it from a matching face amount: two quotes
 * for the same family a fortnight apart look identical in the data and are not
 * the same conversation.
 */
async function quotesForCase(d: Detail, refresh: () => Promise<void>): Promise<HTMLElement> {
  const host = el('div');
  if (!d.policy.leadId) {
    host.append(el('p', {
      style: 'font-size:12.5px;color:var(--ink-3);margin:0',
      text: 'This case is not attached to a lead, so there is no quote history to search. '
        + 'Attach it to a lead and any quote saved for that person will show up here.',
    }));
    return host;
  }

  let quotes: Quote[];
  try {
    quotes = await get<Quote[]>(`/v1/quotes?leadId=${encodeURIComponent(d.policy.leadId)}`);
  } catch (e) {
    host.append(el('div', { class: 'warnbox' },
      e instanceof ApiError ? e.message : 'Could not load the quotes for this person.'));
    return host;
  }

  if (!quotes.length) {
    host.append(el('p', {
      style: 'font-size:12.5px;color:var(--ink-3);margin:0',
      text: 'No quotes were saved for this person. A quote saved from the quoter shows here, and '
        + 'linking it is what lets the agency measure how much of what it quotes it goes on to write.',
    }));
    return host;
  }

  const linked = quotes.find((q) => q.policyId === d.policy.id);
  if (!linked) {
    host.append(el('p', {
      style: 'font-size:12.5px;color:var(--ink-3);margin:0 0 10px',
      text: 'None of these is marked as the one this case came from. Pick it and the agency can '
        + 'measure quote-to-placed instead of guessing at it.',
    }));
  }

  for (const q of quotes) {
    const isThis = q.policyId === d.policy.id;
    const elsewhere = q.policyId !== null && !isThis;
    const when = new Date(q.createdAt).toLocaleDateString('en-US', { dateStyle: 'medium' });
    const marks: HTMLElement[] = [];
    if (isThis) marks.push(chip('this case', 'good'));
    else if (elsewhere) marks.push(chip('linked elsewhere', 'cop'));
    // A premium off a loaded rate card and one somebody typed are not the same
    // claim, and the quoter is careful about the difference. So is this.
    marks.push(chip(q.source === 'card' ? 'from a rate card' : 'entered by hand',
      q.source === 'card' ? 'good' : 'warn'));
    if (!isThis && !elsewhere) {
      marks.push(button('This is the one', 'sm', async () => {
        try {
          await post(`/v1/quotes/${q.id}/applied`, { policyId: d.policy.id });
          toast('Linked.', 'ok');
          await refresh();
        } catch (e) {
          toast(e instanceof ApiError ? e.message : 'Could not link that quote.', 'crit');
        }
      }));
    }
    host.append(row({
      title: el('div', { class: 't', text: `${fmtMoney(q.faceCents)} · ${fmtMoney(q.monthlyCents)}/mo` }),
      sub: el('div', {
        class: 's',
        text: [
          q.applicantName ?? 'No name on the quote',
          q.termYears ? `${q.termYears}-year term` : null,
          q.rateClass,
          q.state,
          `quoted ${when}`,
        ].filter((x): x is string => Boolean(x)).join(' · '),
      }),
      right: marks,
    }));
  }
  return host;
}

/* ------------------------------------------------------------ the form -- */

/**
 * Every field a case has, in the order somebody would say them out loud.
 *
 * It is a long form and that is deliberate — an agency loading the book it
 * already wrote needs somewhere to put all of it, including the dates. Nothing
 * here is required except a status, because a case half-known is still worth
 * recording; the parts nobody has yet stay blank until somebody has them.
 */
async function editorForm(existing: Policy | null, done: () => void | Promise<void>): Promise<HTMLElement> {
  const ref = await reference();
  const carriers = await get<Carrier[]>('/v1/carriers').catch(() => [] as Carrier[]);
  const people = await get<Array<{ id: string; firstName: string; lastName: string }>>('/v1/users').catch(() => []);

  const p = existing;
  const f = {
    carrierId: select([['', 'Not set'], ...carriers.filter((c) => !c.archivedAt || c.id === p?.carrierId)
      .map((c) => [c.id, c.name] as [string, string])], p?.carrierId ?? ''),
    agentId: select([['', 'Whoever is signed in'], ...people.map((u) => [u.id, `${u.firstName} ${u.lastName}`] as [string, string])], p?.agentId ?? ''),
    insuredName: input({ value: p?.insuredName ?? '', placeholder: 'Who the policy covers' }),
    insuredDob: input({ type: 'date', value: dateValue(p?.insuredDob ?? null) }),
    productType: select(options(ref.productTypes), p?.productType ?? 'term'),
    productName: input({ value: p?.productName ?? '', placeholder: 'The carrier’s name for it' }),
    termYears: input({ type: 'number', min: '1', max: '130', value: p?.termYears ? String(p.termYears) : '' }),
    faceCents: input({ value: fromCents(p?.faceCents ?? null), placeholder: '250000', inputmode: 'decimal' }),
    premiumCents: input({ value: fromCents(p?.premiumCents ?? null), placeholder: '87.50', inputmode: 'decimal' }),
    premiumMode: select(options(ref.premiumModes), p?.premiumMode ?? 'monthly'),
    /*
     * A picker, not a number box, and it stops at 28.
     *
     * A recurring draft set for the 29th, 30th or 31st is handled differently
     * by every carrier in February — some pull it forward, some push it back,
     * some skip the month — and a date that silently moves is the intermittent
     * failure this whole feature exists to remove. Offering only the days that
     * exist in every month means nobody has to be told that afterwards.
     */
    draftDay: select(
      [['', 'Not set'], ...Array.from({ length: 28 }, (_, i) => [String(i + 1), ordinal(i + 1)] as [string, string])],
      p?.draftDay ? String(p.draftDay) : '',
    ),
    targetPremiumCents: input({ value: fromCents(p?.targetPremiumCents ?? null), inputmode: 'decimal' }),
    underwritingClass: select(options(ref.underwritingClasses), p?.underwritingClass ?? 'unknown'),
    ratingTable: input({ value: p?.ratingTable ?? '', placeholder: 'A, B, C…' }),
    tobaccoRated: select([['', 'Not known'], ['false', 'No'], ['true', 'Yes']],
      p?.tobaccoRated === null || p?.tobaccoRated === undefined ? '' : String(p.tobaccoRated)),
    isReplacement: select([['false', 'No'], ['true', 'Yes — replacing existing coverage']], String(p?.isReplacement ?? false)),
    policyNumber: input({ value: p?.policyNumber ?? '', placeholder: 'Once the carrier issues one' }),
    applicationNumber: input({ value: p?.applicationNumber ?? '', placeholder: 'Before there is a policy number' }),
    beneficiarySummary: input({ value: p?.beneficiarySummary ?? '', placeholder: 'Spouse 100%, or two children 50/50' }),
    quotedAt: input({ type: 'date', value: dateValue(p?.quotedAt ?? null) }),
    submittedAt: input({ type: 'date', value: dateValue(p?.submittedAt ?? null) }),
    approvedAt: input({ type: 'date', value: dateValue(p?.approvedAt ?? null) }),
    issuedAt: input({ type: 'date', value: dateValue(p?.issuedAt ?? null) }),
    placedAt: input({ type: 'date', value: dateValue(p?.placedAt ?? null) }),
    effectiveAt: input({ type: 'date', value: dateValue(p?.effectiveAt ?? null) }),
    commissionRateBps: input({
      type: 'number', min: '0', max: '300', step: '0.5',
      value: p?.commissionRateBps === null || p?.commissionRateBps === undefined ? '' : String(p.commissionRateBps / 100),
      placeholder: '110',
    }),
    commissionCents: input({ value: fromCents(p?.commissionCents ?? null), inputmode: 'decimal' }),
    commissionPaidAt: input({ type: 'date', value: dateValue(p?.commissionPaidAt ?? null) }),
    chargebackCents: input({ value: fromCents(p?.chargebackCents ?? null), inputmode: 'decimal' }),
    notes: el('textarea', { class: 'inp', rows: '3', placeholder: 'Anything the next person needs to know' }) as HTMLTextAreaElement,
  };
  f.notes.value = p?.notes ?? '';

  const status = select(options(ref.policyStatuses), p?.status ?? 'quoted');

  const group = (title: string, ...rows: HTMLElement[]): HTMLElement =>
    el('div', { style: 'margin-bottom:16px' },
      el('div', {
        class: 'kicker', style: 'display:block;margin-bottom:8px',
        text: title,
      }),
      ...rows);
  const cols = (...kids: HTMLElement[]): HTMLElement => el('div', { class: 'filters', style: 'margin-bottom:0' }, ...kids);

  const form = el('div');

  if (!p) {
    form.append(group('Where it stands now',
      cols(field('Status', status,
        'A case you are typing in from your existing book can start wherever it really is — including in force.'))));
  }

  /*
   * The draft-date advice, live, while the date is being chosen.
   *
   * It is shown HERE rather than only on the finished case because this is the
   * moment the mistake is made. An agent picking the 1st because it sounds tidy
   * needs to be told before they save, not on a screen they open after the
   * lapse notice arrives.
   *
   * The client is fetched once and only when the case has one. If it cannot be
   * loaded, or nobody has recorded how they are paid, the box says so and
   * offers nothing — the same refusal to guess the quoter makes about premiums.
   */
  const draftNote = el('div');
  const paintDraft = (lead: { benefitSchedule: string | null; dob: string | null } | null): void => {
    clear(draftNote);
    if (!lead) return;
    const a = adviseDraftDay(
      lead.benefitSchedule as Parameters<typeof adviseDraftDay>[0],
      dobDayOfMonth(lead.dob ? new Date(lead.dob) : null),
      f.draftDay.value ? Number(f.draftDay.value) : null,
    );
    const tone = a.verdict === 'fails' ? 'warnbox' : a.verdict === 'tight' ? 'note' : a.verdict === 'safe' ? 'okbox' : 'note';
    draftNote.append(el('div', { class: tone, style: 'margin-top:8px' }, a.message));
  };
  if (p?.leadId) {
    void get<{ benefitSchedule: string | null; dob: string | null }>(`/v1/leads/${p.leadId}`)
      .then((lead) => {
        paintDraft(lead);
        f.draftDay.addEventListener('change', () => paintDraft(lead));
      })
      // No box at all rather than a box that says nothing useful. The advice is
      // an extra; a case must still be editable when the client cannot be read.
      .catch(() => { /* silent */ });
  }

  form.append(
    group('The sale',
      cols(field('Carrier', f.carrierId), field('Product', f.productType), field('Product name', f.productName)),
      cols(field('Face amount ($)', f.faceCents), field('Premium ($)', f.premiumCents),
        field('Paid', f.premiumMode), field('Term (years)', f.termYears))),
    group('When the premium is taken',
      el('p', {
        style: 'font-size:12px;color:var(--ink-3);margin-bottom:8px',
        text: 'Most policies that lapse were never cancelled — the draft simply came out before the '
          + 'money went in. If the client is on Social Security, the safe days are worked out from '
          + 'their date of birth.',
      }),
      cols(field('Drafted on the', f.draftDay)),
      draftNote),
    group('Who it covers',
      cols(field('Insured', f.insuredName), field('Date of birth', f.insuredDob),
        field('Beneficiaries', f.beneficiarySummary)),
      cols(field('Credited to', f.agentId))),
    group('Underwriting',
      cols(field('Class', f.underwritingClass), field('Rating table', f.ratingTable),
        field('Tobacco', f.tobaccoRated), field('Replacement', f.isReplacement))),
    group('The carrier’s numbers',
      cols(field('Application number', f.applicationNumber), field('Policy number', f.policyNumber))),
    group('Dates',
      el('p', {
        style: 'font-size:12px;color:var(--ink-3);margin-bottom:8px',
        text: 'Leave these alone on a live case — moving it on stamps them for you. Fill them in when you '
          + 'are typing in business you already wrote, so the reports know when it really happened.',
      }),
      cols(field('Quoted', f.quotedAt), field('Submitted', f.submittedAt), field('Approved', f.approvedAt)),
      cols(field('Issued', f.issuedAt), field('Placed', f.placedAt), field('Effective', f.effectiveAt))),
    group('What the agency earns',
      el('p', {
        style: 'font-size:12px;color:var(--ink-3);margin-bottom:8px',
        text: 'The rate is what you expect. The amount is what the statement actually paid — they are '
          + 'different numbers and the difference is worth seeing.',
      }),
      cols(field('Rate (%)', f.commissionRateBps), field('Target premium ($)', f.targetPremiumCents),
        field('Paid ($)', f.commissionCents), field('Paid on', f.commissionPaidAt)),
      cols(field('Chargeback ($)', f.chargebackCents))),
    group('Notes', f.notes),
  );

  const save = button(p ? 'Save changes' : 'Create the case', 'pri', async () => {
    save.disabled = true;
    const money = (box: HTMLInputElement): number | null | undefined => {
      if (!box.value.trim()) return p ? null : undefined;
      const c = toCents(box.value);
      if (c === null) { toast('One of the amounts is not a number.', 'crit'); throw new Error('bad money'); }
      return c;
    };
    const text = (box: HTMLInputElement | HTMLTextAreaElement): string | null | undefined =>
      (box.value.trim() ? box.value.trim() : (p ? null : undefined));
    const when = (box: HTMLInputElement): string | null | undefined =>
      (box.value ? box.value : (p ? null : undefined));

    try {
      const payload: Record<string, unknown> = {
        carrierId: f.carrierId.value || (p ? null : undefined),
        agentId: f.agentId.value || undefined,
        insuredName: text(f.insuredName),
        insuredDob: when(f.insuredDob),
        productType: f.productType.value,
        productName: text(f.productName),
        termYears: f.termYears.value ? Number(f.termYears.value) : (p ? null : undefined),
        faceCents: money(f.faceCents),
        premiumCents: money(f.premiumCents),
        premiumMode: f.premiumMode.value,
        draftDay: f.draftDay.value ? Number(f.draftDay.value) : (p ? null : undefined),
        targetPremiumCents: money(f.targetPremiumCents),
        underwritingClass: f.underwritingClass.value,
        ratingTable: text(f.ratingTable),
        tobaccoRated: f.tobaccoRated.value === '' ? (p ? null : undefined) : f.tobaccoRated.value === 'true',
        isReplacement: f.isReplacement.value === 'true',
        policyNumber: text(f.policyNumber),
        applicationNumber: text(f.applicationNumber),
        beneficiarySummary: text(f.beneficiarySummary),
        quotedAt: when(f.quotedAt),
        submittedAt: when(f.submittedAt),
        approvedAt: when(f.approvedAt),
        issuedAt: when(f.issuedAt),
        placedAt: when(f.placedAt),
        effectiveAt: when(f.effectiveAt),
        // Entered as a percentage because that is how comp is quoted; stored in
        // basis points because 110.5% is a real rate and a whole number is not
        // enough to hold it.
        commissionRateBps: f.commissionRateBps.value
          ? Math.round(Number(f.commissionRateBps.value) * 100)
          : (p ? null : undefined),
        commissionCents: money(f.commissionCents),
        commissionPaidAt: when(f.commissionPaidAt),
        chargebackCents: money(f.chargebackCents),
        notes: text(f.notes),
      };
      for (const k of Object.keys(payload)) if (payload[k] === undefined) delete payload[k];
      if (!p) payload.status = status.value;

      if (p) await patch(`/v1/policies/${p.id}`, payload);
      else await post('/v1/policies', payload);
      toast(p ? 'Saved.' : 'Case created.', 'ok');
      await done();
    } catch (e) {
      if (e instanceof Error && e.message === 'bad money') { save.disabled = false; return; }
      toast(e instanceof ApiError ? e.message : 'Could not save that case.', 'crit');
      save.disabled = false;
    }
  });

  form.append(save);
  return form;
}

async function openEditor(id: string | null, done: () => void): Promise<void> {
  const body = el('div');
  const close = drawer(id ? 'Edit case' : 'New case', body);
  body.append(skeleton(6));
  try {
    const existing = id ? (await get<Detail>(`/v1/policies/${id}`)).policy : null;
    clear(body);
    body.append(await editorForm(existing, () => { close(); done(); }));
  } catch (e) {
    clear(body);
    body.append(el('div', { class: 'warnbox' }, e instanceof ApiError ? e.message : 'Could not open that.'));
  }
}

/* ----------------------------------------------------------- carriers -- */

/**
 * Change a carrier already on the list.
 *
 * Only the fields a person actually edited are sent. The route reads
 * `sentKeys(ctx)` and treats a present-but-empty value as "clear this", so
 * posting every field on every save would blank the four an agency filled in
 * months ago the first time somebody corrected a phone number.
 */
function editCarrier(c: Carrier, after: () => Promise<void>): void {
  const name = input({ value: c.name });
  const shortName = input({ value: c.shortName ?? '', placeholder: 'MoO' });
  const rating = input({ value: c.amBestRating ?? '', placeholder: 'A+' });
  const phone = input({ value: c.phone ?? '', placeholder: 'Where a case is chased' });
  const portal = input({ value: c.agentPortalUrl ?? '', placeholder: 'https://…' });
  const notes = el('textarea', { class: 'inp', rows: '3' }) as HTMLTextAreaElement;
  notes.value = c.notes ?? '';

  const save = button('Save', 'pri', async () => {
    const body: Record<string, string | null> = {};
    const put = (key: string, next: string, was: string | null): void => {
      const trimmed = next.trim();
      if (trimmed === (was ?? '')) return;      // untouched — do not send it
      body[key] = trimmed === '' ? null : trimmed;
    };
    put('name', name.value, c.name);
    put('shortName', shortName.value, c.shortName);
    put('amBestRating', rating.value, c.amBestRating);
    put('phone', phone.value, c.phone);
    put('agentPortalUrl', portal.value, c.agentPortalUrl);
    put('notes', notes.value, c.notes);

    if (Object.keys(body).length === 0) { close(); return; }
    if (body.name !== undefined && (body.name ?? '').trim().length < 2) {
      toast('Give the carrier a name of at least two characters.', 'crit');
      return;
    }
    save.disabled = true;
    try {
      await patch(`/v1/carriers/${c.id}`, body);
      toast('Saved.', 'ok');
      close();
      await after();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not save that.', 'crit');
      save.disabled = false;
    }
  });

  const close = drawer(c.name, el('div', null,
    el('p', {
      style: 'font-size:12.5px;color:var(--ink-3);margin:0 0 14px',
      text: 'Renaming a carrier renames it everywhere, including on cases already written against it. '
        + 'That is usually what you want when a name was mistyped, and never what you want if you meant '
        + 'to add a second carrier.',
    }),
    el('div', { class: 'filters' }, field('Carrier', name), field('Short name', shortName)),
    el('div', { class: 'filters' }, field('AM Best', rating), field('Phone', phone)),
    field('Agent portal', portal),
    field('Notes', notes, 'Anything the next person filing a case here should know.'),
    el('div', { style: 'margin-top:16px' }, save)));
}

/**
 * Which carriers a person is appointed with.
 *
 * A licence and an appointment are different facts and this screen exists
 * because nothing else records the second one. A producer licensed in Michigan
 * still cannot place a Mutual of Omaha case until Mutual of Omaha has appointed
 * them, and the writing number the commission pays against lives here and
 * nowhere else — which is why an agency that keeps it in somebody's inbox
 * loses it when that person leaves.
 *
 * Your own appointments are yours to record. Somebody else's needs
 * `user.manage`, exactly as licences do.
 */
export function appointmentsPanel(me: Me, forUserId?: string): HTMLElement {
  const host = el('div');
  const list = el('div');
  const mine = !forUserId || forUserId === me.user.id;
  const target = forUserId ?? me.user.id;
  const mayManage = mine || me.capabilities.includes('user.manage');

  const carrierSel = select([['', 'Loading carriers…']]);
  const writing = input({ placeholder: 'The carrier’s code for this producer' });
  const states = input({ placeholder: 'MI, OH, IN — blank means everywhere they are licensed' });
  const effective = input({ type: 'date' });
  const expires = input({ type: 'date' });

  let carriers: Carrier[] = [];

  const add = button('Record appointment', 'pri', async () => {
    if (!carrierSel.value) { toast('Pick a carrier.', 'crit'); return; }
    add.disabled = true;
    try {
      await post('/v1/appointments', {
        carrierId: carrierSel.value,
        ...(mine ? {} : { forUserId: target }),
        ...(writing.value.trim() ? { writingNumber: writing.value.trim() } : {}),
        ...(states.value.trim()
          ? { states: states.value.split(',').map((s) => s.trim()).filter(Boolean) }
          : {}),
        ...(effective.value ? { effectiveAt: effective.value } : {}),
        ...(expires.value ? { expiresAt: expires.value } : {}),
      });
      for (const box of [writing, states, effective, expires]) box.value = '';
      toast('Recorded.', 'ok');
      await load();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not record that appointment.', 'crit');
    } finally { add.disabled = false; }
  });

  const nameOf = (carrierId: string): string =>
    carriers.find((c) => c.id === carrierId)?.name ?? 'A carrier no longer on the list';

  async function load(): Promise<void> {
    clear(list);
    list.append(skeleton(2));
    // One template literal, always, even when the query is empty — the same
    // rule the production report follows above. A URL assembled by a
    // conditional is a URL that cannot be found by reading the source, and
    // `cases.wiring.test.ts` reads the source to prove every button reaches a
    // route the API actually serves.
    const q = new URLSearchParams();
    if (!mine) q.set('userId', target);
    let rows: Appointment[];
    try {
      [carriers, rows] = await Promise.all([
        get<Carrier[]>('/v1/carriers').catch(() => [] as Carrier[]),
        get<Appointment[]>(`/v1/appointments?${q}`),
      ]);
    } catch (e) {
      clear(list);
      list.append(empty('Could not load appointments',
        e instanceof ApiError ? e.message : 'Something went wrong fetching them.'));
      return;
    }

    clear(carrierSel);
    const open = new Set(rows.filter((a) => !a.terminatedAt).map((a) => a.carrierId));
    const pickable = carriers.filter((c) => !c.archivedAt && !open.has(c.id));
    carrierSel.append(el('option', { value: '' }, pickable.length ? 'Pick a carrier' : 'No carriers left to add'));
    for (const c of pickable) carrierSel.append(el('option', { value: c.id }, c.name));

    clear(list);
    const live = rows.filter((a) => !a.terminatedAt);
    const ended = rows.filter((a) => a.terminatedAt);
    if (!live.length && !ended.length) {
      list.append(empty(mine ? 'No appointments recorded' : 'Nothing recorded for them',
        'An appointment is the carrier’s authorisation to sell its products, and it is separate from a '
        + 'state licence. Recording them here keeps the writing numbers somewhere other than an inbox.'));
      return;
    }

    const rowFor = (a: Appointment): HTMLElement => {
      const expiring = a.expiresAt && !a.terminatedAt
        ? (new Date(a.expiresAt).getTime() - Date.now()) / 86_400_000
        : null;
      const marks: HTMLElement[] = [];
      if (a.terminatedAt) marks.push(chip('ended', 'cop'));
      else if (expiring !== null && expiring < 0) marks.push(chip('expired', 'crit'));
      else if (expiring !== null && expiring < 60) marks.push(chip(`expires in ${Math.round(expiring)}d`, 'warn'));
      if (mayManage && !a.terminatedAt) {
        marks.push(button('End', 'sm ghost', async () => {
          if (!confirm(`End the ${nameOf(a.carrierId)} appointment?\n\nCases already submitted keep it. It stops counting toward new ones.`)) return;
          try { await post(`/v1/appointments/${a.id}/end`, {}); await load(); }
          catch (e) { toast(e instanceof ApiError ? e.message : 'Could not end it.', 'crit'); }
        }));
      }
      const bits = [
        a.writingNumber ? `Writing number ${a.writingNumber}` : 'No writing number recorded',
        a.states.length ? a.states.join(', ') : 'Every state they are licensed in',
        a.effectiveAt ? `from ${a.effectiveAt.slice(0, 10)}` : null,
        a.expiresAt ? `to ${a.expiresAt.slice(0, 10)}` : null,
      ].filter((x): x is string => Boolean(x));
      return row({
        title: el('div', { class: 't', text: nameOf(a.carrierId) }),
        sub: el('div', { class: 's', text: bits.join(' · ') }),
        right: marks,
      });
    };

    for (const a of live) list.append(rowFor(a));
    if (ended.length) {
      list.append(el('div', {
        class: 'kicker', style: 'display:block;margin-top:16px',
        text: `Ended (${ended.length})`,
      }));
      for (const a of ended) list.append(rowFor(a));
    }
  }

  host.append(list);
  if (mayManage) {
    host.append(el('div', { class: 'filters', style: 'margin-top:14px' },
      field('Carrier', carrierSel), field('Writing number', writing)));
    host.append(el('div', { class: 'filters' },
      field('States', states, 'Blank means every state they are licensed in.'),
      field('Effective', effective), field('Expires', expires)));
    host.append(add);
  }
  void load();
  return host;
}

/**
 * The carrier list, for Settings.
 *
 * Lives with the rest of the agency's configuration rather than on the cases
 * screen, because that is what it is. Everyone reads it — filing a case means
 * picking from it — but only an owner or administrator adds to it. With three
 * people free-typing carrier names, an agency's largest carrier ends up split
 * three ways in every production report, and no screen fixes that afterwards.
 */
export function carriersPanel(me: Me): HTMLElement {
  const host = el('div');
  const list = el('div');
  const quick = el('div');
  const mayEdit = me.capabilities.includes('config.production');

  const name = input({ placeholder: 'Mutual of Omaha' });
  const shortName = input({ placeholder: 'MoO' });
  const rating = input({ placeholder: 'A+' });
  const phone = input({ placeholder: 'Where a case is chased' });
  const portal = input({ placeholder: 'https://…' });

  const add = button('Add carrier', 'pri', async () => {
    if (!name.value.trim()) { toast('Give the carrier a name.', 'crit'); return; }
    add.disabled = true;
    try {
      await post('/v1/carriers', {
        name: name.value.trim(),
        shortName: shortName.value.trim() || undefined,
        amBestRating: rating.value.trim() || undefined,
        phone: phone.value.trim() || undefined,
        agentPortalUrl: portal.value.trim() || undefined,
      });
      for (const box of [name, shortName, rating, phone, portal]) box.value = '';
      toast('Added.', 'ok');
      await load();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not add that carrier.', 'crit');
    } finally { add.disabled = false; }
  });

  /**
   * One tap per carrier, for the ones most agencies here write.
   *
   * The same list the front page names, imported rather than retyped, so the
   * two cannot drift. It exists because the alternative is seven trips through
   * a form on somebody's first afternoon, and because a carrier typed by hand
   * seven times is a carrier spelled seven ways — which splits an agency's
   * largest carrier across several rows of its own production report, with no
   * way to merge them afterwards.
   *
   * Ones already on the list drop out, so this empties itself as it is used.
   */
  const quickAdd = (existing: Carrier[]): HTMLElement | null => {
    const have = new Set(existing.map((c) => c.name.toLowerCase()));
    const missing = CARRIER_NAMES.filter((n) => !have.has(n.toLowerCase()));
    if (!missing.length) return null;

    const wrap = el('div', { style: 'margin-top:16px' });
    const chips = el('div', { style: 'display:flex;flex-wrap:wrap;gap:8px;margin-top:10px' });

    const addOne = async (name: string, btn: HTMLButtonElement): Promise<void> => {
      btn.disabled = true;
      try {
        await post('/v1/carriers', { name });
      } catch (e) {
        // Already there is not a failure worth shouting about; anything else is.
        if (!(e instanceof ApiError && e.code === 'carrier_exists')) {
          toast(e instanceof ApiError ? e.message : `Could not add ${name}.`, 'crit');
          btn.disabled = false;
          throw e;
        }
      }
    };

    for (const name of missing) {
      const b = button(`+ ${name}`, 'sm', async () => {
        try { await addOne(name, b); await load(); } catch { /* reported already */ }
      });
      chips.append(b);
    }

    const all = button(`Add all ${missing.length}`, 'sm pri', async () => {
      all.disabled = true;
      try {
        for (const name of missing) {
          await post('/v1/carriers', { name }).catch((e) => {
            if (!(e instanceof ApiError && e.code === 'carrier_exists')) throw e;
          });
        }
        toast(`Added ${missing.length} carriers.`, 'ok');
        await load();
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not add them all.', 'crit');
        all.disabled = false;
      }
    });

    wrap.append(
      el('div', { class: 'kicker', style: 'display:block', text: 'Add the ones you write' }),
      el('p', {
        style: 'font-size:12.5px;color:var(--ink-3);margin-top:6px',
        text: 'One tap each, spelled the same way every time. Anything not here you can add below.',
      }),
      chips,
      el('div', { style: 'margin-top:10px' }, all));
    return wrap;
  };

  async function load(): Promise<void> {
    clear(list);
    const rows = await get<Carrier[]>('/v1/carriers').catch(() => [] as Carrier[]);
    if (!rows.length) {
      clear(quick);
      if (mayEdit) { const q = quickAdd(rows); if (q) quick.append(q); }
      list.append(empty('No carriers yet',
        'Add the carriers you write. Every case picks from this list, which is what keeps one carrier '
        + 'from appearing three times in your production report under three spellings.'));
      return;
    }
    clear(quick);
    if (mayEdit) { const q = quickAdd(rows); if (q) quick.append(q); }

    // Every cell below is a Node, never a string. `table()` assigns a string
    // cell with innerHTML, and a carrier name is typed by a person at the
    // agency — so a name containing markup would run in the browser of
    // everybody else who opened this screen. Nodes carry no such risk.
    list.append(table(['Carrier', 'Short', 'Rating', 'Phone', ''], rows.map((c) => ({
      cells: [
        el('span', {
          text: c.name,
          ...(c.archivedAt ? { style: 'color:var(--ink-4)' } : { style: 'font-weight:600' }),
        }),
        el('span', { text: c.shortName ?? '—' }),
        el('span', { text: c.amBestRating ?? '—' }),
        el('span', { text: c.phone ?? '—' }),
        c.archivedAt
          ? chip('archived', 'cop')
          : (mayEdit
            ? el('div', { style: 'display:flex;gap:6px;justify-content:flex-end' },
              button('Edit', 'sm ghost', () => editCarrier(c, load)),
              button('Archive', 'sm ghost', async () => {
                // Archived, never deleted: an agency that stops writing a
                // carrier still services that business for decades, and every
                // case already written points at it.
                if (!confirm(`Archive ${c.name}?\n\nCases already written against it keep their carrier. It just stops appearing on new ones.`)) return;
                try { await post(`/v1/carriers/${c.id}/archive`); await load(); }
                catch (e) { toast(e instanceof ApiError ? e.message : 'Could not archive it.', 'crit'); }
              }))
            : el('span')),
      ],
    }))));
  }

  host.append(list);
  if (mayEdit) {
    host.append(quick);
    host.append(el('div', { class: 'filters', style: 'margin-top:14px' },
      field('Carrier', name), field('Short name', shortName), field('AM Best', rating)));
    host.append(el('div', { class: 'filters' }, field('Phone', phone), field('Agent portal', portal)));
    host.append(add);
  } else {
    host.append(el('p', {
      style: 'font-size:12.5px;color:var(--ink-3);margin-top:12px',
      text: 'Only an agency owner or administrator can change this list. Ask yours to add a carrier you need.',
    }));
  }
  void load();
  return host;
}
